import type { SeoConfig } from '@klaro7/shared-types';

import type { PublicProfessional } from '@/features/professionals/services/professional-public.service';
import { resolveUploadUrl } from '@/lib/uploads';

export type JsonLdObject = Record<string, unknown>;

export function buildOrganizationJsonLd(config: SeoConfig, origin: string): JsonLdObject | null {
  const { jsonLd, global } = config;
  if (!jsonLd.organizationEnabled) return null;
  const data: JsonLdObject = {
    '@context': 'https://schema.org',
    '@type': 'Organization',
    name: jsonLd.organizationName || global.siteName,
    url: origin,
    description: global.defaultDescription,
  };
  if (jsonLd.organizationLogo) data.logo = jsonLd.organizationLogo;
  if (jsonLd.organizationSameAs.length > 0) data.sameAs = jsonLd.organizationSameAs;
  return data;
}

export function buildWebSiteJsonLd(config: SeoConfig, origin: string): JsonLdObject | null {
  if (!config.jsonLd.websiteEnabled) return null;
  return {
    '@context': 'https://schema.org',
    '@type': 'WebSite',
    name: config.global.siteName,
    url: origin,
    inLanguage: 'it',
  };
}

export function buildConsultantJsonLd(
  config: SeoConfig,
  professional: PublicProfessional,
  pageUrl: string
): JsonLdObject | null {
  if (!config.jsonLd.consultantPersonEnabled) return null;
  const profile = professional.userId?.profile;
  const nome = `${profile?.firstName ?? ''} ${profile?.lastName ?? ''}`.trim();
  if (!nome) return null;

  const data: JsonLdObject = {
    '@context': 'https://schema.org',
    '@type': 'Person',
    name: nome,
    jobTitle: professional.title,
    url: pageUrl,
    knowsAbout: professional.specializations,
    worksFor: {
      '@type': 'Organization',
      name: config.jsonLd.organizationName || config.global.siteName,
    },
  };
  const avatar = profile?.avatar ? resolveUploadUrl(profile.avatar) : undefined;
  if (avatar) data.image = avatar;
  if (professional.rating && professional.reviewCount && professional.reviewCount > 0) {
    data.aggregateRating = {
      '@type': 'AggregateRating',
      ratingValue: Number(professional.rating.toFixed(1)),
      reviewCount: professional.reviewCount,
      bestRating: 5,
      worstRating: 1,
    };
  }
  return data;
}

export interface FaqEntry {
  question: string;
  answer: string;
}

export function buildFaqPageJsonLd(config: SeoConfig, faqs: FaqEntry[]): JsonLdObject | null {
  if (!config.jsonLd.faqPageEnabled || faqs.length === 0) return null;
  return {
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    mainEntity: faqs.map((f) => ({
      '@type': 'Question',
      name: f.question,
      acceptedAnswer: { '@type': 'Answer', text: f.answer },
    })),
  };
}

/**
 * Extracts question/answer pairs from rendered FAQ prose: each <h3> is a
 * question, the following siblings (until the next heading) are its answer.
 */
export function extractFaqEntries(root: HTMLElement): FaqEntry[] {
  const entries: FaqEntry[] = [];
  for (const h3 of root.querySelectorAll('h3')) {
    const question = h3.textContent?.trim();
    if (!question) continue;
    const parts: string[] = [];
    let node = h3.nextElementSibling;
    while (node && !/^H[1-3]$/.test(node.tagName)) {
      const text = node.textContent?.replace(/\s+/g, ' ').trim();
      if (text) parts.push(text);
      node = node.nextElementSibling;
    }
    if (parts.length > 0) entries.push({ question, answer: parts.join(' ') });
  }
  return entries;
}
